/**
 * Check diagnostico del ledger (#116-bis) — sezione Impostazioni → Sistema.
 *
 * Tre controlli, due gravità: invariante e disallineamento CER sono ANOMALIE
 * (rosse, alimentano il contatore in Dashboard); i saldi negativi sono
 * "da verificare" (neutri, fuori dal contatore) perché sono ammessi per scelta
 * — lo scarico può portare un saldo sotto zero e il valore reale non viene
 * forzato. Colorarli di rosso li renderebbe rumore che si impara a ignorare.
 *
 * Da qui non si CORREGGONO dati — quelli si correggono dove nascono, sull'ordine
 * o sullo scarico — ma due azioni ci sono: configurare la soglia di scarto e
 * prendere atto delle riduzioni gia' gestite. La scrittura e' piu' stretta della
 * lettura: la pagina la vedono in quattro ruoli, queste due le fanno in tre.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { getDiagnosticaLedger, setPresaVisione, type DiagnosticaLedger, type RigaScaricoRidotto } from '../../api/diagnostica';
import { getImpiantoSettings, updateImpiantoSettings } from '../../api/impianto_settings';
import { useAuthStore } from '../../store/authStore';
import { canWriteSogliaLedger } from '../../lib/constants';
import { apiErrorMessage } from '../../lib/apiError';
import { fmtKg, fmtPct } from '../../lib/saldo';
import type { Peso } from '../../api/diagnostica';

/** Numero + unita'. Il FORMATO e' quello condiviso di `lib/saldo`: qui si
 *  aggiunge solo il "kg", perche' queste tabelle non hanno un'intestazione che
 *  porti l'unita'. Prima era un secondo formatter scritto a mano, e la stringa
 *  "2E+4" ci passava attraverso intatta. */
function kg(n: Peso | null): string {
  if (n === null || n === undefined) return '—';
  return `${fmtKg(n)} kg`;
}

const TH = 'px-3 py-2 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide';
const TD = 'px-3 py-2 text-sm text-ink-dark';

interface BloccoProps {
  titolo: string;
  descrizione: string;
  totale: number;
  troncato: boolean;
  mostrate: number;
  gravita: 'anomalia' | 'da_verificare';
  vuoto: string;
  testid: string;
  children: React.ReactNode;
}

function Blocco({ titolo, descrizione, totale, troncato, mostrate, gravita, vuoto, testid, children }: BloccoProps) {
  const { t } = useTranslation();
  const anomalia = gravita === 'anomalia';
  return (
    <section data-testid={testid}>
      <div className="flex items-start gap-2 mb-1.5">
        {totale === 0
          ? <CheckCircle2 size={16} className="text-ink-faint flex-shrink-0 mt-0.5" />
          : anomalia
            ? <AlertTriangle size={16} className="text-accent-danger flex-shrink-0 mt-0.5" />
            : <Eye size={16} className="text-ink-mid flex-shrink-0 mt-0.5" />}
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-ink-dark">
            {titolo}
            {totale > 0 && (
              <span
                className={`ml-2 text-xs px-1.5 py-0.5 rounded ${anomalia ? 'bg-accent-danger-bg text-accent-danger' : 'bg-stone-bg text-ink-mid'}`}
                data-testid={`${testid}-totale`}
              >
                {anomalia ? t('settings.diagnostica.badge_anomalie', { count: totale })
                          : t('settings.diagnostica.badge_da_verificare', { count: totale })}
              </span>
            )}
          </h3>
          <p className="text-xs text-ink-soft mt-0.5">{descrizione}</p>
        </div>
      </div>

      {totale === 0 ? (
        <p className="text-sm text-ink-soft px-3 py-3 bg-stone-bg rounded-lg">{vuoto}</p>
      ) : (
        <div className="border border-stone-mid rounded-lg overflow-x-auto">
          <table className="w-full">{children}</table>
        </div>
      )}

      {/* Il taglio va dichiarato: una tabella che ne mostra 200 su 340 senza
          dirlo si legge come "ho visto tutto". */}
      {troncato && (
        <p className="text-xs text-ink-soft mt-1.5" data-testid={`${testid}-troncato`}>
          {t('settings.diagnostica.troncato', { mostrate, totale })}
        </p>
      )}
    </section>
  );
}

export default function DiagnosticaLedgerSection() {
  const { t } = useTranslation();
  const [dati, setDati] = useState<DiagnosticaLedger | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [soglia, setSoglia] = useState<string>('');
  const [sogliaEsito, setSogliaEsito] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const puoScrivere = canWriteSogliaLedger(useAuthStore((st) => st.user?.role));

  const carica = useCallback(() => {
    void (async () => {
      setLoading(true);
      setErrore(null);
      try {
        const [ledger, impianto] = await Promise.all([
          getDiagnosticaLedger(), getImpiantoSettings(),
        ]);
        setDati(ledger);
        setSoglia(String(impianto.soglia_scarto_prelievo_pct));
      } catch (err: unknown) {
        setErrore(apiErrorMessage(err, t('settings.diagnostica.errore_caricamento')));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);
  useEffect(() => { carica(); }, [carica]);

  async function salvaSoglia() {
    const n = Number(soglia);
    setBusy(true); setSogliaEsito(null); setErrore(null);
    try {
      const agg = await updateImpiantoSettings({ soglia_scarto_prelievo_pct: n });
      setSoglia(String(agg.soglia_scarto_prelievo_pct));
      setSogliaEsito(t('settings.diagnostica.soglia_salvata'));
    } catch (err: unknown) {
      setErrore(apiErrorMessage(err, t('settings.diagnostica.soglia_errore')));
    } finally { setBusy(false); }
  }

  async function togglePresaVisione(riga: RigaScaricoRidotto) {
    setBusy(true); setErrore(null);
    try {
      await setPresaVisione(riga.gruppo_id, riga.stato !== 'verificato');
      carica();
    } catch (err: unknown) {
      setErrore(apiErrorMessage(err, t('settings.diagnostica.presa_visione_errore')));
    } finally { setBusy(false); }
  }

  if (loading) return <p className="text-sm text-ink-soft">{t('settings.diagnostica.caricamento')}</p>;
  if (errore) {
    return (
      <div className="p-3 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger"
           data-testid="diagnostica-errore">
        {errore}
      </div>
    );
  }
  if (!dati) return null;

  const { invariante_saldi: inv, cer_disallineato: cer, saldi_negativi: neg,
          scarichi_ridotti: rid } = dati;
  const perStato = (st: RigaScaricoRidotto['stato']) => rid.righe.filter((r) => r.stato === st);

  return (
    <div className="space-y-6" data-testid="diagnostica-ledger">
      <p className="text-sm text-ink-soft">{t('settings.diagnostica.descrizione')}</p>

      <Blocco
        titolo={t('settings.diagnostica.invariante_titolo')}
        descrizione={t('settings.diagnostica.invariante_desc')}
        totale={inv.totale} troncato={inv.troncato} mostrate={inv.righe.length}
        gravita="anomalia" vuoto={t('settings.diagnostica.invariante_vuoto')}
        testid="diag-invariante"
      >
        <>
          <thead className="bg-stone-bg border-b border-stone-mid">
            <tr>
              <th className={TH}>{t('settings.diagnostica.col_area')}</th>
              <th className={TH}>{t('settings.diagnostica.col_materiale')}</th>
              <th className={TH}>{t('settings.diagnostica.col_saldo')}</th>
              <th className={TH}>{t('settings.diagnostica.col_movimenti')}</th>
              <th className={TH}>{t('settings.diagnostica.col_scarto')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-mid">
            {inv.righe.map((r) => (
              <tr key={`${r.area_id}-${r.materiale}`}>
                <td className={TD}>{r.area_codice ?? '—'}</td>
                <td className={`${TD} font-mono`}>{r.materiale}</td>
                <td className={`${TD} tabular-nums`}>{kg(r.saldo_kg)}</td>
                <td className={`${TD} tabular-nums`}>{kg(r.somma_movimenti)}</td>
                <td className={`${TD} tabular-nums text-accent-danger font-medium`}>{kg(r.scarto_kg)}</td>
              </tr>
            ))}
          </tbody>
        </>
      </Blocco>

      <Blocco
        titolo={t('settings.diagnostica.cer_titolo')}
        descrizione={t('settings.diagnostica.cer_desc')}
        totale={cer.totale} troncato={cer.troncato} mostrate={cer.righe.length}
        gravita="anomalia" vuoto={t('settings.diagnostica.cer_vuoto')}
        testid="diag-cer"
      >
        <>
          <thead className="bg-stone-bg border-b border-stone-mid">
            <tr>
              <th className={TH}>{t('settings.diagnostica.col_ordine')}</th>
              <th className={TH}>{t('settings.diagnostica.col_conferimento')}</th>
              <th className={TH}>{t('settings.diagnostica.col_area')}</th>
              <th className={TH}>{t('settings.diagnostica.col_materiale_ledger')}</th>
              <th className={TH}>{t('settings.diagnostica.col_cer_ordine')}</th>
              <th className={TH}>{t('settings.diagnostica.col_netto')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-mid">
            {cer.righe.map((r) => (
              <tr key={`${r.conferimento_id}-${r.area_id}-${r.materiale}`}>
                <td className={TD}>{r.codice_ordine ?? '—'}</td>
                <td className={TD}>{r.numero ?? '—'}</td>
                <td className={TD}>{r.area_codice ?? '—'}</td>
                <td className={`${TD} font-mono text-accent-danger`}>{r.materiale}</td>
                <td className={`${TD} font-mono`}>{r.cer_ordine ?? '—'}</td>
                <td className={`${TD} tabular-nums`}>{kg(r.netto_kg)}</td>
              </tr>
            ))}
          </tbody>
        </>
      </Blocco>

      <Blocco
        titolo={t('settings.diagnostica.negativi_titolo')}
        descrizione={t('settings.diagnostica.negativi_desc')}
        totale={neg.totale} troncato={neg.troncato} mostrate={neg.righe.length}
        gravita="da_verificare" vuoto={t('settings.diagnostica.negativi_vuoto')}
        testid="diag-negativi"
      >
        <>
          <thead className="bg-stone-bg border-b border-stone-mid">
            <tr>
              <th className={TH}>{t('settings.diagnostica.col_area')}</th>
              <th className={TH}>{t('settings.diagnostica.col_materiale')}</th>
              <th className={TH}>{t('settings.diagnostica.col_saldo')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-mid">
            {neg.righe.map((r) => (
              <tr key={`${r.area_id}-${r.materiale}`}>
                <td className={TD}>{r.area_codice ?? '—'}</td>
                <td className={`${TD} font-mono`}>{r.materiale}</td>
                <td className={`${TD} tabular-nums font-medium`}>{kg(r.saldo_kg)}</td>
              </tr>
            ))}
          </tbody>
        </>
      </Blocco>

      {/* ── Controllo 4: prelievi ridotti ─────────────────────────────────── */}
      <section data-testid="diag-ridotti">
        <div className="flex items-start gap-2 mb-1.5">
          {rid.totale === 0
            ? <CheckCircle2 size={16} className="text-ink-faint flex-shrink-0 mt-0.5" />
            : rid.totale_da_verificare > 0
              ? <AlertTriangle size={16} className="text-accent-danger flex-shrink-0 mt-0.5" />
              : <Eye size={16} className="text-ink-mid flex-shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink-dark">
              {t('settings.diagnostica.ridotti_titolo')}
              {rid.totale_da_verificare > 0 && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-accent-danger-bg text-accent-danger"
                      data-testid="diag-ridotti-totale">
                  {t('settings.diagnostica.badge_da_verificare', { count: rid.totale_da_verificare })}
                </span>
              )}
            </h3>
            <p className="text-xs text-ink-soft mt-0.5">{t('settings.diagnostica.ridotti_desc')}</p>
          </div>
        </div>

        {rid.totale === 0 ? (
          <p className="text-sm text-ink-soft px-3 py-3 bg-stone-bg rounded-lg">
            {t('settings.diagnostica.ridotti_vuoto')}
          </p>
        ) : (
          <div className="space-y-4">
            {/* Tre popolazioni, tre sezioni: chi apre la pagina deve capire a
                colpo d'occhio quali righe chiedono un'azione e quali no. */}
            {([
              ['da_verificare', 'ridotti_sez_da_verificare', true],
              ['sotto_soglia', 'ridotti_sez_sotto_soglia', false],
              ['verificato', 'ridotti_sez_verificate', false],
            ] as const).map(([stato, chiave, azione]) => {
              const righe = perStato(stato);
              if (righe.length === 0) return null;
              return (
                <div key={stato} data-testid={`diag-ridotti-${stato}`}>
                  <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide mb-1">
                    {t(`settings.diagnostica.${chiave}`, { count: righe.length })}
                  </p>
                  <div className="border border-stone-mid rounded-lg overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-stone-bg border-b border-stone-mid">
                        <tr>
                          <th className={TH}>{t('settings.diagnostica.col_data')}</th>
                          <th className={TH}>{t('settings.diagnostica.col_area')}</th>
                          <th className={TH}>{t('settings.diagnostica.col_materiale')}</th>
                          <th className={TH}>{t('settings.diagnostica.col_richiesto')}</th>
                          <th className={TH}>{t('settings.diagnostica.col_registrato')}</th>
                          <th className={TH}>{t('settings.diagnostica.col_scarto')}</th>
                          <th className={TH}></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-mid">
                        {righe.map((r) => (
                          <tr key={r.gruppo_id}>
                            <td className={TD}>{new Date(r.created_at).toLocaleDateString('it-IT')}</td>
                            <td className={TD}>{r.aree ?? '—'}</td>
                            <td className={`${TD} font-mono`}>{r.materiali ?? '—'}</td>
                            <td className={`${TD} tabular-nums`}>{kg(r.peso_richiesto_kg)}</td>
                            <td className={`${TD} tabular-nums`}>{kg(r.peso_registrato_kg)}</td>
                            <td className={`${TD} tabular-nums ${azione ? 'text-accent-danger font-medium' : ''}`}>
                              {kg(r.scarto_kg)} ({fmtPct(r.scarto_pct)}%)
                            </td>
                            <td className={`${TD} text-right whitespace-nowrap`}>
                              {stato === 'sotto_soglia' ? (
                                <span className="text-xs text-ink-faint">
                                  {t('settings.diagnostica.nessuna_azione')}
                                </span>
                              ) : puoScrivere ? (
                                <button type="button" disabled={busy}
                                  onClick={() => { void togglePresaVisione(r); }}
                                  data-testid={`diag-presa-visione-${r.gruppo_id}`}
                                  className="inline-flex items-center gap-1 text-xs text-ink-mid hover:text-sage-dark disabled:opacity-50">
                                  {stato === 'verificato'
                                    ? <><EyeOff size={13} /> {t('settings.diagnostica.annulla_presa_visione')}</>
                                    : <><Eye size={13} /> {t('settings.diagnostica.prendi_visione')}</>}
                                </button>
                              ) : (
                                <span className="text-xs text-ink-faint">
                                  {stato === 'verificato' && r.verificato_da_nome}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Soglia di scarto: l'unica configurazione della pagina ─────────── */}
      <section className="border-t border-stone-mid pt-5" data-testid="diag-soglia">
        <h3 className="text-sm font-semibold text-ink-dark">{t('settings.diagnostica.soglia_titolo')}</h3>
        {/* La spiegazione sta ACCANTO al campo, non nel messaggio d'errore: chi
            imposta la soglia deve capire cosa configura PRIMA di sbagliare. */}
        <p className="text-xs text-ink-soft mt-0.5 mb-2 max-w-2xl">
          {t('settings.diagnostica.soglia_desc')}
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number" min={1} max={50} step={1}
            data-testid="diag-soglia-input"
            disabled={!puoScrivere || busy}
            value={soglia}
            onChange={(e) => setSoglia(e.target.value)}
            title={puoScrivere ? undefined : t('settings.diagnostica.soglia_sola_lettura')}
            className="w-24 text-sm border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage disabled:bg-stone-bg disabled:text-ink-soft"
          />
          <span className="text-sm text-ink-soft">%</span>
          {puoScrivere && (
            <button type="button" onClick={() => { void salvaSoglia(); }} disabled={busy}
              data-testid="diag-soglia-salva"
              className="px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark disabled:opacity-60">
              {t('settings.diagnostica.soglia_salva')}
            </button>
          )}
        </div>
        {!puoScrivere && (
          <p className="text-xs text-ink-faint mt-1" data-testid="diag-soglia-sola-lettura">
            {t('settings.diagnostica.soglia_sola_lettura')}
          </p>
        )}
        {sogliaEsito && (
          <p className="text-xs text-sage-dark mt-1" data-testid="diag-soglia-esito">{sogliaEsito}</p>
        )}
      </section>
    </div>
  );
}
